import React from 'react';
import { Redirect } from 'react-router-dom';

import { PAGE_CODES } from 'constants/pageCodes';
import { lazyWithRetry } from 'utils/chunkLoadRecovery';
import { EnhancedPath } from 'utils/history';

export const PROGRAMS_ROUTING_PARAMS = {
  criteriaId: 'criteriaId',
  snapshotId: 'snapshotId',
};

// The Programs list, Channels editor, OB program-channel mapper and Program Index Types editor live in
// Margin Center → Products & Channels (DEV-5399, LEN-804), each as a tab of that page — kept as
// ProgramsPaths members so every existing import site keeps resolving through this one source of
// truth. LEGACY_PATHS below are the pre-move URLs, redirected to these new ones.
export class ProgramsPaths {
  static eligibilityBuilder = new EnhancedPath('/eligibility-builder');
  static miRules = new EnhancedPath('/mi-rules');
  static rateLockTerms = new EnhancedPath('/rate-lock-terms');
  static adjustments = new EnhancedPath('/adjustments');
  static llpaCaps = new EnhancedPath('/llpa-caps');
  static customAdjustments = new EnhancedPath('/custom-adjustments');
  static pricingWorkbench = new EnhancedPath('/pricing-workbench');
  static programIndexTypes = new EnhancedPath('/margin-center/products/index-types');
  static programs = new EnhancedPath('/margin-center/products/programs');
  static buyDowns = new EnhancedPath('/buy-downs');
  static channels = new EnhancedPath('/margin-center/products/channels');
  static escrowRequirements = new EnhancedPath('/escrow-requirements');
  static programsMapper = new EnhancedPath('/margin-center/products/channel-mapping');
  static llpaTypes = new EnhancedPath('/llpa-types');
  static miCoveragePercentage = new EnhancedPath('/mi-coverage-percentage-criteria');

  static miRulesCriteria = new EnhancedPath(
    `${ProgramsPaths.miRules.path}/:${PROGRAMS_ROUTING_PARAMS.criteriaId}?/:${PROGRAMS_ROUTING_PARAMS.snapshotId}?`,
  );

  static rateLockTermsCriteria = new EnhancedPath(
    `${ProgramsPaths.rateLockTerms.path}/:${PROGRAMS_ROUTING_PARAMS.criteriaId}?/:${PROGRAMS_ROUTING_PARAMS.snapshotId}?`,
  );

  static eligibilityBuilderCriteria = new EnhancedPath(
    `${ProgramsPaths.eligibilityBuilder.path}/:${PROGRAMS_ROUTING_PARAMS.criteriaId}?/:${PROGRAMS_ROUTING_PARAMS.snapshotId}?`,
  );

  static escrowRequirementsCriteria = new EnhancedPath(
    `${ProgramsPaths.escrowRequirements.path}/:${PROGRAMS_ROUTING_PARAMS.criteriaId}?`,
  );

  static adjustmentsCriteria = new EnhancedPath(
    `${ProgramsPaths.adjustments.path}/:${PROGRAMS_ROUTING_PARAMS.criteriaId}?/:${PROGRAMS_ROUTING_PARAMS.snapshotId}?`,
  );

  static llpaCapCriteria = new EnhancedPath(
    `${ProgramsPaths.llpaCaps.path}/:${PROGRAMS_ROUTING_PARAMS.criteriaId}?/:${PROGRAMS_ROUTING_PARAMS.snapshotId}?`,
  );

  static miCoveragePercentageCriteria = new EnhancedPath(
    `${ProgramsPaths.miCoveragePercentage.path}/:${PROGRAMS_ROUTING_PARAMS.criteriaId}?/:${PROGRAMS_ROUTING_PARAMS.snapshotId}?`,
  );
}

export const PRICING_WORKBENCH_QUERY_PARAM = {
  applicationId: 'applicationId',
};

// Builds the Pricing Workbench URL, optionally deep-linking to a saved lead's application so the
// workbench reopens rehydrated + repriced from that lead's scenario (LEN-73). The application id is
// a query param, not a path param: the workbench route is flat and the id is optional context.
export const buildPricingWorkbenchPath = applicationId => {
  if (!applicationId) {
    return ProgramsPaths.pricingWorkbench.path;
  }

  const query = new URLSearchParams({
    [PRICING_WORKBENCH_QUERY_PARAM.applicationId]: applicationId,
  });

  return `${ProgramsPaths.pricingWorkbench.path}?${query.toString()}`;
};

// Pre-DEV-5399 URLs for the pages moved into Margin Center — redirected below so bookmarks/deep
// links keep working.
const LEGACY_PATHS = {
  programs: '/programs',
  channels: '/channels',
  programsMapper: '/programs-mapper',
  programIndexTypes: '/program-index-types',
};

const routesConfig = {
  settings: {
    layout: {},
  },
  routes: [
    {
      path: LEGACY_PATHS.programs,
      component: () => <Redirect to={ProgramsPaths.programs.path} />,
      pageCode: PAGE_CODES.PROGRAM,
      exact: true,
    },
    {
      path: LEGACY_PATHS.channels,
      component: () => <Redirect to={ProgramsPaths.channels.path} />,
      pageCode: PAGE_CODES.CHANNEL,
      exact: true,
    },
    {
      path: LEGACY_PATHS.programsMapper,
      component: () => <Redirect to={ProgramsPaths.programsMapper.path} />,
      pageCode: PAGE_CODES.CHANNEL_PROGRAM,
      exact: true,
    },
    {
      path: LEGACY_PATHS.programIndexTypes,
      component: () => <Redirect to={ProgramsPaths.programIndexTypes.path} />,
      pageCode: PAGE_CODES.INTERNAL_INDEX_TYPE,
      exact: true,
    },
    {
      path: ProgramsPaths.buyDowns.path,
      component: lazyWithRetry(() => import('app/pages/programs/buyDowns')),
      pageCode: PAGE_CODES.BUY_DOWNS,
    },
    {
      path: ProgramsPaths.customAdjustments.path,
      component: lazyWithRetry(() => import('app/pages/programs/customAdjustments')),
      pageCode: PAGE_CODES.CUSTOM_ADJUSTMENTS,
    },
    {
      path: ProgramsPaths.escrowRequirementsCriteria.path,
      component: lazyWithRetry(() => import('app/pages/programs/escrowRequirements')),
      pageCode: PAGE_CODES.ESCROW_REQUIREMENTS,
    },
    {
      path: ProgramsPaths.eligibilityBuilderCriteria.path,
      component: lazyWithRetry(() => import('app/pages/programs/eligibilityBuilder')),
      pageCode: PAGE_CODES.ELIGIBILITY_BUILDER,
    },
    {
      path: ProgramsPaths.adjustmentsCriteria.path,
      component: lazyWithRetry(() => import('app/pages/programs/adjustments')),
      pageCode: PAGE_CODES.ADJUSTMENTS,
    },
    {
      path: ProgramsPaths.llpaCapCriteria.path,
      component: lazyWithRetry(() => import('app/pages/programs/llpaCap')),
      pageCode: PAGE_CODES.LLPA_CAP,
    },
    {
      path: ProgramsPaths.miCoveragePercentageCriteria.path,
      component: lazyWithRetry(() => import('app/pages/programs/miCoverageCriteria')),
      pageCode: PAGE_CODES.MI_COVERAGE_CRITERIA,
    },
    {
      path: ProgramsPaths.miRulesCriteria.path,
      component: lazyWithRetry(() => import('app/pages/programs/miRules')),
      pageCode: PAGE_CODES.MI_RULES,
    },
    {
      path: ProgramsPaths.miRulesCriteria.path,
      component: lazyWithRetry(() => import('app/pages/programs/miRules')),
      pageCode: PAGE_CODES.MI_RULES,
    },
    {
      path: ProgramsPaths.rateLockTermsCriteria.path,
      component: lazyWithRetry(() => import('app/pages/programs/rateLockTerms')),
      pageCode: PAGE_CODES.RATE_LOCK_TERMS,
    },
    {
      path: ProgramsPaths.pricingWorkbench.path,
      component: lazyWithRetry(() => import('app/pages/programs/pricingWorkbench')),
      pageCode: PAGE_CODES.PRICING_WORKBENCH,
    },
    {
      path: ProgramsPaths.llpaTypes.path,
      component: lazyWithRetry(() => import('app/pages/programs/llpaTypes')),
      pageCode: PAGE_CODES.LLPA_TYPE,
    },
  ],
};

export default routesConfig;
